const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Etiquetas de Contactos y Clasificación CRM",
    summary:
      "Las etiquetas son el sistema de categorización visual y segmentación más potente de BotMix. Permiten asociar colores corporativos y estados clave a tus contactos para clasificar audiencias, acelerar la atención y alimentar campañas segmentadas.",
  },
  sections: [
    {
      id: "conceptos_etiquetas",
      title: "1. Categorización Visual y Código de Colores",
      description:
        "Identificación ágil del cliente mediante distintivos cromáticos.",
      items: [
        {
          id: "codigo_colores",
          title: "Paleta de Colores Personalizada",
          definition:
            "Asigna un color representativo a cada etiqueta (ej: verde para 'Pagado', amarillo para 'Pendiente', rojo para 'Atención Urgente'). Los colores son visibles en chats y tablas.",
        },
        {
          id: "etiquetas_multiples",
          title: "Asignación Múltiple por Contacto",
          definition:
            "Un mismo contacto puede tener tantas etiquetas como sea necesario para describir su perfil comercial completo.",
        },
      ],
    },
    {
      id: "segmentacion_clientes",
      title: "2. Segmentación Estratégica y Ciclo de Vida",
      description:
        "Agrupación de contactos para campañas y seguimiento especializado.",
      items: [
        {
          id: "ciclo_vida",
          title: "Estados del Cliente (Embudo)",
          definition:
            "Define etiquetas para cada fase de compra (ej: 'Nuevo Prospecto', 'Cotización Enviada', 'Cliente Recurrente', 'Soporte').",
        },
        {
          id: "origen_campana",
          title: "Canales de Origen y Campañas",
          definition:
            "Etiqueta automáticamente o de forma manual a los prospectos según la fuente publicitaria (ej: 'Campaña Facebook', 'Webinar Octubre', 'Referido').",
        },
      ],
    },
    {
      id: "uso_en_filtros",
      title: "3. Filtros Cruzados y Búsqueda Rápida",
      description:
        "Localización instantánea de audiencias en la plataforma.",
      items: [
        {
          id: "filtrado_contactos",
          title: "Filtro en Directorio de Contactos",
          definition:
            "Filtra la lista de contactos seleccionando una o más etiquetas para auditar bases o realizar acciones en lote.",
        },
        {
          id: "filtrado_tickets",
          title: "Filtro en Bandeja de Tickets",
          definition:
            "Los agentes pueden priorizar conversaciones activas filtrando directamente por la etiqueta asignada.",
        },
      ],
    },
    {
      id: "etiquetas_kanban_diferencia",
      title: "4. Etiquetas de Contactos vs Etapas de Kanban",
      description:
        "Claridad entre los dos sistemas de organización de BotMix.",
      items: [
        {
          id: "etiqueta_contacto_vs_etapa",
          title: "Etiquetas de Directorio vs Carriles Kanban",
          definition:
            "Las etiquetas de esta sección se adhieren al perfil del contacto de forma transversal. Las etapas de Kanban representan las fases operativas por las que avanza una conversación dentro de un embudo específico.",
        },
      ],
    },
  ],
};

const en = {
  header: {
    badge: "BotMix Official Guide",
    title: "Contact Tags & CRM Classification",
    summary:
      "Tags provide the most flexible visual categorization and segmentation system in BotMix. Assign corporate color palettes and key statuses to contacts to classify customer audiences, streamline chat workflows, and power targeted campaigns.",
  },
  sections: [
    {
      id: "conceptos_etiquetas",
      title: "1. Visual Categorization & Color Palette",
      description:
        "Fast identification of customer profiles through chromatic badges.",
      items: [
        {
          id: "codigo_colores",
          title: "Custom Color Palette",
          definition:
            "Assign distinctive hex colors to each tag (e.g., green for 'Paid', yellow for 'Pending', red for 'Urgent Attention'). These colors render prominently across chats and tables.",
        },
        {
          id: "etiquetas_multiples",
          title: "Multiple Tags per Contact",
          definition:
            "A single contact can have multiple tags applied simultaneously to capture multifaceted customer attributes.",
        },
      ],
    },
    {
      id: "segmentacion_clientes",
      title: "2. Strategic Segmentation & Customer Lifecycle",
      description:
        "Group contacts for targeted broadcasts and specialized service flows.",
      items: [
        {
          id: "ciclo_vida",
          title: "Lifecycle Stages",
          definition:
            "Tag contacts based on their journey (e.g., 'New Lead', 'Quote Sent', 'VIP Customer', 'Support Ticket').",
        },
        {
          id: "origen_campana",
          title: "Lead Sources & Campaign Tracking",
          definition:
            "Tag leads automatically or manually by acquisition channel (e.g., 'Facebook Ads', 'October Webinar', 'Referral').",
        },
      ],
    },
    {
      id: "uso_en_filtros",
      title: "3. Cross Filtering & Instant Search",
      description:
        "Instant audience retrieval across the entire BotMix platform.",
      items: [
        {
          id: "filtrado_contactos",
          title: "Contact Directory Filtering",
          definition:
            "Isolate specific subsets of contacts by selecting one or more tags for mass review or batch actions.",
        },
        {
          id: "filtrado_tickets",
          title: "Ticket Inbox Filtering",
          definition:
            "Agents can prioritize chats by filtering the conversation feed according to assigned tags.",
        },
      ],
    },
    {
      id: "etiquetas_kanban_diferencia",
      title: "4. Contact Tags vs Kanban Lanes",
      description:
        "Key distinction between BotMix organizational structures.",
      items: [
        {
          id: "etiqueta_contacto_vs_etapa",
          title: "Directory Tags vs Kanban Lanes",
          definition:
            "Directory tags attach permanently to the customer profile. Kanban lanes represent sequential workflow stages that a conversation moves through inside an active sales pipeline.",
        },
      ],
    },
  ],
};

const pt = {
  header: {
    badge: "Guia Oficial BotMix",
    title: "Etiquetas de Contatos e Classificação CRM",
    summary:
      "As etiquetas são o mecanismo de categorização visual e segmentação mais ágil do BotMix. Permitem vincular cores corporativas e status chave aos seus contatos para classificar públicos, agilizar o suporte e impulsionar campanhas segmentadas.",
  },
  sections: [
    {
      id: "conceptos_etiquetas",
      title: "1. Categorização Visual e Código de Cores",
      description:
        "Identificação imediata do cliente através de marcadores cromáticos.",
      items: [
        {
          id: "codigo_colores",
          title: "Paleta de Cores Personalizada",
          definition:
            "Atribua cores específicas a cada etiqueta (ex: verde para 'Pago', amarelo para 'Pendente', vermelho para 'Urgente'). As cores aparecem com destaque em chats e listas.",
        },
        {
          id: "etiquetas_multiples",
          title: "Múltiplas Etiquetas por Contato",
          definition:
            "Um mesmo contato pode receber diversas etiquetas para mapear seu perfil comercial com fidelidade.",
        },
      ],
    },
    {
      id: "segmentacion_clientes",
      title: "2. Segmentação Estratégica e Ciclo de Vida",
      description:
        "Agrupamento de contatos para disparos segmentados e atendimento.",
      items: [
        {
          id: "ciclo_vida",
          title: "Fases do Cliente",
          definition:
            "Crie etiquetas para cada estágio (ex: 'Novo Lead', 'Orçamento Enviado', 'Cliente VIP', 'Em Cobrança').",
        },
        {
          id: "origen_campana",
          title: "Canais de Origem e Campanhas",
          definition:
            "Classifique os leads pela fonte de captação (ex: 'Campanha Meta', 'Indicação', 'Evento').",
        },
      ],
    },
    {
      id: "uso_en_filtros",
      title: "3. Filtros Cruzados e Busca Rápida",
      description:
        "Localização instantânea de contatos na plataforma.",
      items: [
        {
          id: "filtrado_contactos",
          title: "Filtro no Diretório de Contatos",
          definition:
            "Filtre a lista de contatos por uma ou mais etiquetas para ações e conferências em massa.",
        },
        {
          id: "filtrado_tickets",
          title: "Filtro na Caixa de Entrada",
          definition:
            "Os operadores podem filtrar os atendimentos abertos pela etiqueta atribuída para dar prioridade.",
        },
      ],
    },
    {
      id: "etiquetas_kanban_diferencia",
      title: "4. Etiquetas de Contato vs Etapas do Kanban",
      description:
        "Entenda a diferença entre as duas ferramentas do BotMix.",
      items: [
        {
          id: "etiqueta_contacto_vs_etapa",
          title: "Etiquetas do Diretório vs Colunas do Kanban",
          definition:
            "As etiquetas desta tela pertencem ao perfil fixo do contato. As etapas do Kanban indicam a fase dinâmica em que uma conversa se encontra dentro de um funil de vendas.",
        },
      ],
    },
  ],
};

const fr = {
  header: {
    badge: "Guide Officiel BotMix",
    title: "Étiquettes de Contacts et Classification CRM",
    summary:
      "Les étiquettes constituent le système de classification visuelle et de ciblage le plus puissant de BotMix. Associez des couleurs personnalisées et des statuts clés à vos contacts pour segmenter vos audiences et optimiser le support.",
  },
  sections: [
    {
      id: "conceptos_etiquetas",
      title: "1. Classification Visuelle et Code Couleur",
      description:
        "Identification instantanée du client grâce à des repères visuels.",
      items: [
        {
          id: "codigo_colores",
          title: "Palette de Couleurs Personnalisée",
          definition:
            "Attribuez une couleur représentative à chaque étiquette (ex: vert pour 'Payé', jaune pour 'En attente', rouge pour 'Urgent'). Visible dans les listes et les chats.",
        },
        {
          id: "etiquetas_multiples",
          title: "Étiquettes Multiples par Contact",
          definition:
            "Un contact peut accumuler plusieurs étiquettes pour décrire précisément sa situation commerciale.",
        },
      ],
    },
    {
      id: "segmentacion_clientes",
      title: "2. Segmentation Stratégique et Cycle de Vie",
      description:
        "Groupement de contacts pour campagnes ciblées et relances.",
      items: [
        {
          id: "ciclo_vida",
          title: "Étapes du Cycle Client",
          definition:
            "Créez des étiquettes par phase (ex: 'Nouveau Prospect', 'Devis Envoyé', 'Client Fidèle', 'Support').",
        },
        {
          id: "origen_campana",
          title: "Sources et Origine des Prospects",
          definition:
            "Identifiez la provenance publicitaire (ex: 'Facebook Ads', 'Webinaire', 'Recommandation').",
        },
      ],
    },
    {
      id: "uso_en_filtros",
      title: "3. Filtres Croisés et Recherche Rapide",
      description:
        "Retrouvez instantanément vos segments au sein de BotMix.",
      items: [
        {
          id: "filtrado_contactos",
          title: "Filtre dans le Répertoire des Contacts",
          definition:
            "Filtrez vos contacts selon une ou plusieurs étiquettes pour des opérations ou audits groupés.",
        },
        {
          id: "filtrado_tickets",
          title: "Filtre dans la Boîte de Réception",
          definition:
            "Les agents peuvent trier les tickets selon les étiquettes pour traiter en priorité les demandes urgentes.",
        },
      ],
    },
    {
      id: "etiquetas_kanban_diferencia",
      title: "4. Étiquettes de Contact vs Étapes Kanban",
      description:
        "Distinction entre les deux logiques d'organisation.",
      items: [
        {
          id: "etiqueta_contacto_vs_etapa",
          title: "Étiquettes de Répertoire vs Colonnes Kanban",
          definition:
            "Les étiquettes de cette section restent attachées au contact de manière durable. Les étapes Kanban représentent la progression opérationnelle d'un ticket dans un entonnoir.",
        },
      ],
    },
  ],
};

const ar = {
  header: {
    badge: "دليل BotMix الرسمي",
    title: "وسوم جهات الاتصال والتصنيف في CRM",
    summary:
      "تعد الوسوم أقوى وسيلة للتصنيف البصري والتقسيم المخصص في BotMix. تتيح ربط ألوان مميزة وحالات رئيسية بجهات الاتصال لتصنيف الجمهور، تسريع خدمة العملاء وتغذية الحملات الموجهة.",
  },
  sections: [
    {
      id: "conceptos_etiquetas",
      title: "1. التصنيف البصري وتناسق الألوان",
      description:
        "تحديد هوية وحالة العميل بسرعة من خلال شارات لونية مميزة.",
      items: [
        {
          id: "codigo_colores",
          title: "لوحة ألوان مخصصة",
          definition:
            "عيّن لوناً مميزاً لكل وسم (مثل: الأخضر لـ 'تم السداد'، الأصفر لـ 'معلق'، الأحمر لـ 'عاجل جداً'). تظهر الألوان بوضوح داخل المحادثات والجداول.",
        },
        {
          id: "etiquetas_multiples",
          title: "وسوم متعددة لكل جهة اتصال",
          definition:
            "يمكن منح العميل الواحد عدة وسوم مختلفة لتغطية كافة أبعاد ملفه التجاري باحترافية.",
        },
      ],
    },
    {
      id: "segmentacion_clientes",
      title: "2. التقسيم الاستراتيجي ودورة حياة العميل",
      description:
        "تجميع العملاء لإرسال الحملات المستهدفة والمتابعة الدقيقة.",
      items: [
        {
          id: "ciclo_vida",
          title: "مراحل مسار العميل",
          definition:
            "حدد وسوماً لكل مرحلة بيعية (مثل: 'عميل جديد'، 'عرض سعر مرسل'، 'عميل دائم'، 'دعم فني').",
        },
        {
          id: "origen_campana",
          title: "قنوات الاستقطاب والحملات الإعلانية",
          definition:
            "تصنيف العملاء حسب المصدر الإعلاني (مثل: 'إعلانات فيسبوك'، 'ندوة أكتوبر'، 'توصية مباشرة').",
        },
      ],
    },
    {
      id: "uso_en_filtros",
      title: "3. التصفية المتقاطعة والبحث السريع",
      description:
        "الوصول الفوري للشرائح والجمهور المستهدف عبر المنصة.",
      items: [
        {
          id: "filtrado_contactos",
          title: "التصفية في دليل جهات الاتصال",
          definition:
            "تصفية قائمة جهات الاتصال بتحديد وسم أو أكثر لتنفيذ إجراءات جماعية أو تدقيق القوائم.",
        },
        {
          id: "filtrado_tickets",
          title: "التصفية في صندوق محادثات التذاكر",
          definition:
            "يمكن للموظفين منح الأولوية للمحادثات من خلال تصفية التذاكر النشطة حسب الوسوم المعينة.",
        },
      ],
    },
    {
      id: "etiquetas_kanban_diferencia",
      title: "4. وسوم جهات الاتصال مقارنة بمراحل كانبان",
      description:
        "الفارق الجوهري بين آليات التنظيم في BotMix.",
      items: [
        {
          id: "etiqueta_contacto_vs_etapa",
          title: "وسوم الدليل مقارنة بأعمدة كانبان",
          definition:
            "تلتصق وسوم هذا القسم بملف جهة الاتصال بصورة دائمة، بينما تمثل أعمدة كانبان المراحل الإجرائية المتتابعة التي تنتقل عبرها المحادثة داخل قمع مبيعات محدد.",
        },
      ],
    },
  ],
};

export const TAGS_HELP_DATA = {
  es,
  en,
  pt,
  fr,
  ar,
};
